import { formatBytes, formatDateTime, formatDuration, formatRelative } from "@amber/ui";
import { MEDIA_TYPE_LABELS, type ItemRow } from "../../contracts/index";
import type { ColumnId } from "./columns";
import { formatSourceDate } from "./ItemDrawer";
import { ProgressBar } from "./ProgressBar";
import { ReadinessCell, StatusCell } from "./StatusCell";

function StageCell({ item }: { item: ItemRow }) {
  const [stage, ...rest] = item.stage_detail.split(" · ");
  return (
    <span title={item.stage_detail}>
      <b className="font-strong text-fg">{stage}</b>
      {rest.length > 0 ? (
        <span className={item.status.status === "failed" ? "text-attention" : undefined}> · {rest.join(" · ")}</span>
      ) : null}
    </span>
  );
}

function DurationCell({ item }: { item: ItemRow }) {
  const parts = [
    item.duration_seconds === null ? null : formatDuration(item.duration_seconds),
    item.size_bytes === null ? null : formatBytes(item.size_bytes),
  ].filter(Boolean);
  return <span className="font-mono text-md">{parts.length === 0 ? "—" : parts.join(" · ")}</span>;
}

export function BacklogCell({ column, item }: { column: ColumnId; item: ItemRow }) {
  switch (column) {
    case "people":
      return (
        <span title={item.people.map((p) => p.name).join(", ")}>
          {item.people.length === 0 ? "—" : item.people.map((p) => p.name).join(", ")}
        </span>
      );
    case "platform":
      return (
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="inline-grid h-4 min-w-[22px] flex-none place-items-center rounded-[3px] bg-surface-3 px-1 font-mono text-[9.5px] font-semibold text-muted"
          >
            {item.platform.code}
          </span>
          <span className="truncate text-fg">{item.platform.label}</span>
        </span>
      );
    case "channel":
      return <span title={item.channel ?? undefined}>{item.channel ?? "—"}</span>;
    case "progress":
      return <ProgressBar item={item} />;
    case "stage":
      return <StageCell item={item} />;
    case "status":
      return <StatusCell status={item.status} />;
    case "readiness":
      return <ReadinessCell readiness={item.readiness} />;
    case "type":
      return <>{MEDIA_TYPE_LABELS[item.media_type]}</>;
    case "duration":
      return <DurationCell item={item} />;
    case "language":
      return <>{item.language?.toUpperCase() ?? "—"}</>;
    case "published":
      return <>{formatSourceDate(item.published)}</>;
    case "discovered":
      return <span title={formatDateTime(item.discovered_at)}>{formatRelative(item.discovered_at)}</span>;
  }
}
